<template>
  <!-- enable backdrop click while providing other options for accessibility -->
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <dialog
    ref="dialog"
    @click="handleClick($event)"
    @close="emit('close')"
    class="login-menu-dialog"
  >
    <header class="login-menu-dialog__header">
      <strong>Online-Zugänge</strong>
      <button @click="hide()" class="login-menu-dialog-closer">
        <IconClose></IconClose>
      </button>
    </header>
    <div class="login-menu-dialog__content">
      <ul class="login-menu" view-transition-name="login-menu">
        <li
          v-for="link in links"
          :key="link.path"
          class="login-menu__item"
          :class="{ 'login-menu__item--marked': link.marked }"
        >
          <a
            :href="link.path"
            :target="link.path ? '_external' : '_self'"
            class="login-menu__item__link"
            :class="{ 'login-menu__item__link--first-marked': link === firstMarked }"
          >
            <IconChrevron />{{ link.title }}
          </a>
        </li>
      </ul>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

import { type INavigationItem } from '../helper';

import IconChrevron from '~icons/mmm/chevron-right';
import IconClose from '~icons/mmm/close';

export interface ILoginMenuDialog {
  hide: () => void;
  show: () => void;
  dialog: HTMLDialogElement;
}

const props = defineProps<{
  links: INavigationItem[];
}>();

const emit = defineEmits(['open', 'close']);

const dialog = ref<HTMLDialogElement>();

const firstMarked = computed(() => {
  return props.links.filter(link => link.marked)[0];
});

function handleClick(event: MouseEvent) {
  if (!dialog.value) return;
  const rect = dialog.value.getBoundingClientRect();
  const isInDialog =
    rect.top <= event.clientY &&
    event.clientY <= rect.top + rect.height &&
    rect.left <= event.clientX &&
    event.clientX <= rect.left + rect.width;
  if (!isInDialog) {
    hide();
  }
}

function show() {
  dialog.value?.showModal();
  emit('open');
}

function hide() {
  dialog.value?.close();
  emit('close');
}

defineExpose({ show, hide, dialog });
</script>

<style lang="scss" scoped>
@use '../../../common/styles/utils/utils';

.login-menu-dialog {
  border-radius: 5px;
  background-color: var(--color-background-background);
  padding: 0 0 24px 0;
  width: calc(100% - 48px);
  max-width: 475px;
  border: none;

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.28);
  }

  &__header {
    padding: 30px 24px 15px 38px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__content {
    padding: 0;
  }
}

.login-menu {
  list-style-type: none;
  overflow: auto;
  justify-content: space-between;
  flex-direction: column;
  display: flex;
  margin: 0;
  padding: 0;

  &__item {
    display: flex;
    flex: 1 1 100%;
    justify-content: space-between;
    font-size: utils.px-to-rem(16);
    padding: 0 38px;

    svg {
      width: 10px;
      height: 16px;
      margin-right: 10px;
    }

    &__link {
      padding: utils.px-to-rem(12) 0;
      color: var(--color-mobile-menu);
      text-decoration: none;
      flex: 1 1 100%;
      height: 100%;
      display: flex;
      align-items: center;
      border-top: 1px solid var(--color-grey-400);
      &--first-marked {
        border-color: transparent;
      }
    }

    &--marked {
      color: var(--default-text-color);
      background-color: var(--overlay-background);
      padding: 0 38px;
    }

    &__after {
      margin: utils.px-to-rem(12) 0;
      flex: 0 0 utils.px-to-rem(8);
      height: utils.px-to-rem(8);
      width: utils.px-to-rem(8);
      border: 1px solid var(--color-primary);
      color: var(--color-primary);
      border-radius: 50%;
      padding: 3px;
      box-sizing: content-box;
      transform: translate(6px, 2px);
    }
  }
}

.login-menu-dialog-closer {
  height: 40px;
  width: 40px;
  padding: 8px;
  background-color: transparent;
  border: none;
  position: relative;
  cursor: pointer;

  svg {
    position: relative;
    height: 25px;
    width: 25px;
    color: inherit;
    z-index: 2;
  }
}
</style>
